import React, { useEffect, useState } from 'react';
import { RadioGroup } from '@headlessui/react';

import useAsync from '../hooks/useAsync';
import useModal from '../hooks/useModal';

import { Field, FormRow, Label } from '../components/Form/tailwindForm';
import Slider from '../components/Shell/Slider';
import { classNames } from '../components/helpers/functions';
import { SelectCreatable } from '../components/SelectCreatable_old';

import Button from '../components/Button';
import { Input } from '../components/Form';

import { addContact, getContacts } from '../api/contacts';

const titleOptions = [
	{ label: 'Field Engineer', key: 'field-engineer' },
	{ label: 'Support', key: 'support' },
	{ label: 'Service Manager', key: 'service-manager' },
	{ label: 'Sales', key: 'sales' },
	{ label: 'Finance', key: 'finance' },
	{ label: 'Executive', key: 'executive' },
];

const AddContactSlider = () => {
	const { open, setClose } = useModal('addContact');
	const [selectedType, setSelectedType] = useState('person');
	const [form, setForm] = useState({});

	const {
		execute: onAdd,
		error,
		status,
	} = useAsync(addContact, {
		immediate: false,
	});

	const { value: contacts } = useAsync(getContacts);

	useEffect(() => {
		if (!open) {
			setForm({});
			setSelectedType('person');
		}
	}, [open]);

	const handleSetForm = (key, value) => {
		let temp = form;

		if (value === '') {
			delete temp[key];
		} else {
			temp[key] = value;
		}

		setForm(temp);
	};

	const hasError = error;
	const isLoading = status === 'pending';

	const uniqueCompanies = [
		...new Map(
			contacts?.rows
				?.filter((c) => c.type === 'company')
				?.map((p) => ({
					label: p.companyName,
					value: p.id,
				}))
				.map((item) => [item['label'], item])
		).values(),
	];

	return (
		<Slider name="addContact" title="Add a contact" form>
			{hasError ? (
				<div
					className="p-4 mb-4 text-xs text-red-700 bg-red-100 rounded-lg"
					role="alert"
				>
					<span className="font-medium">Uh oh, </span>something went wrong
				</div>
			) : null}
			<FormRow>
				<Label>Contact Type</Label>
				<Field>
					<RadioGroup
						value={selectedType}
						onChange={(e) => {
							setSelectedType(e);
							handleSetForm('type', e);
						}}
					>
						<RadioGroup.Label className="sr-only">
							Contact Types
						</RadioGroup.Label>
						<div className="relative -space-y-px rounded-md bg-white">
							{[
								{ name: 'Person', value: 'person' },
								{ name: 'Company', value: 'company' },
							].map((plan, planIdx) => (
								<RadioGroup.Option
									key={plan.name}
									value={plan.value}
									className={({ checked }) =>
										classNames(
											planIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '',
											planIdx === 1 ? 'rounded-bl-md rounded-br-md' : '',
											checked
												? 'bg-blue-50 border-blue-200 z-10'
												: 'border-gray-200',
											'relative border p-4 flex flex-col cursor-pointer md:pl-4 md:pr-6 md:grid md:grid-cols-3 focus:outline-none'
										)
									}
								>
									{({ active, checked }) => (
										<>
											<span className="flex items-center text-sm">
												<span
													className={classNames(
														checked
															? 'bg-blue-500 border-transparent'
															: 'bg-white border-gray-300',
														active ? 'ring-2 ring-offset-2 ring-gray-900' : '',
														'h-4 w-4 rounded-full border flex items-center justify-center'
													)}
													aria-hidden="true"
												>
													<span className="rounded-full bg-white w-1.5 h-1.5" />
												</span>
												<RadioGroup.Label
													as="span"
													className="ml-3 font-medium text-gray-900"
												>
													{plan.name}
												</RadioGroup.Label>
											</span>
										</>
									)}
								</RadioGroup.Option>
							))}
						</div>
					</RadioGroup>
				</Field>
			</FormRow>

			<FormRow>
				<Label>Email</Label>
				<Field>
					<Input
						placeholder={'Email'}
						onChange={(e) => handleSetForm('email', e.target.value)}
					/>
				</Field>
			</FormRow>

			<FormRow>
				<Label>Phone Number</Label>
				<Field>
					<Input
						placeholder={'Phone Number'}
						onChange={(e) => handleSetForm('phoneNumber', e.target.value)}
					/>
				</Field>
			</FormRow>

			{selectedType === 'person' && (
				<>
					<FormRow>
						<Label>First Name</Label>
						<Field>
							<Input
								placeholder={'First Name'}
								onChange={(e) => handleSetForm('firstName', e.target.value)}
							/>
						</Field>
					</FormRow>
					<FormRow>
						<Label>Last Name</Label>
						<Field>
							<Input
								placeholder={'Last Name'}
								onChange={(e) => handleSetForm('lastName', e.target.value)}
							/>
						</Field>
					</FormRow>
					<FormRow>
						<Label>Title</Label>
						<Field>
							<SelectCreatable
								options={titleOptions || []}
								isClearable
								isSearchable
								placeholder="Select or create title"
								onChange={(e) => {
									handleSetForm('title', e || null);
								}}
								style={{ width: '100%' }}
							/>
						</Field>
					</FormRow>
					<FormRow>
						<Label>Company</Label>
						<Field>
							<SelectCreatable
								options={uniqueCompanies || []}
								placeholder={'Select or create company'}
								isSearchable
								isClearable
								onChange={(e) => {
									handleSetForm('company', e);
								}}
							/>
						</Field>
					</FormRow>
				</>
			)}
			{selectedType === 'company' && (
				<>
					<FormRow>
						<Label>Company Name</Label>
						<Field>
							<Input
								placeholder={'Company Name'}
								onChange={(e) => handleSetForm('companyName', e.target.value)}
							/>
						</Field>
					</FormRow>
				</>
			)}

			<FormRow>
				<Label>Location</Label>
				<Field>
					<Input
						placeholder={'Location'}
						onChange={(e) => handleSetForm('location', e.target.value)}
					/>
				</Field>
			</FormRow>

			<FormRow className="pt-4" alignRight>
				<Button
					onClick={() => {
						onAdd(
							configurePayload({ ...form, type: selectedType }),
							(newItem) => {
								setClose();
							}
						);
					}}
				>
					{isLoading ? 'Adding...' : 'Add contact'}
				</Button>
			</FormRow>
		</Slider>
	);
};

const configurePayload = ({ title, company, ...rest }) => ({
	...rest,
	title: title?.label,
	...(company?.__isNew__
		? { createNewCompany: company?.label }
		: company?.value
		? { companyId: company?.value }
		: null),
});

export default AddContactSlider;
